import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import dayjs from '../util/date.js';

import { setProjectFilterDates } from '../context/projectFilter.js';
import { useStateValue } from '../context/state.jsx';

const formatShort = dateValue => dayjs(dateValue).format('DD-MMM-YY');

const MapRangeSlider = ({ minDate, maxDate }) => {
	const [{ projectFilter }, dispatch] = useStateValue();
	const boundMin = Number(minDate) || 0;
	const boundMax = Number(maxDate) || 0;
	const span = Math.max(boundMax - boundMin, 1);

	const rangeMin = Number(projectFilter.startDate) || boundMin;
	const rangeMax = Number(projectFilter.endDate) || boundMax;
	const [value, setValue] = useState({
		min: rangeMin,
		max: rangeMax
	});

	// Keep local thumb state aligned when filter dates change elsewhere (reset, layout, etc.).
	useEffect(() => {
		setValue({
			min: Number(projectFilter.startDate) || boundMin,
			max: Number(projectFilter.endDate) || boundMax
		});
	}, [projectFilter.startDate, projectFilter.endDate, boundMin, boundMax]);

	const startPct = useMemo(
		() => Math.max(0, Math.min(100, ((value.min - boundMin) / span) * 100)),
		[value.min, boundMin, span]
	);
	const endPct = useMemo(
		() => Math.max(0, Math.min(100, ((value.max - boundMin) / span) * 100)),
		[value.max, boundMin, span]
	);

	const applyLocal = nextValue => {
		const [min, max] = nextValue;
		if (min < boundMin || max > boundMax) {
			return;
		}
		setValue({ min, max });
	};

	const commit = nextValue => {
		const [min, max] = nextValue;
		dispatch(setProjectFilterDates(Number(min) || 0, Number(max) || 0));
	};

	if (!boundMin || !boundMax || boundMax <= boundMin) {
		return null;
	}

	return (
		<div className="map-range-slider">
			<div className="map-range-slider__row">
				<span className="map-range-slider__scale map-range-slider__scale--start">
					{formatShort(boundMin)}
				</span>
				<div className="map-range-slider__track-wrap">
					{/* Query dates sit on the rail outside the blue selected segment */}
					<span
						className="map-range-slider__query map-range-slider__query--start"
						style={{ left: `${startPct}%` }}>
						{formatShort(value.min)}
					</span>
					<span
						className="map-range-slider__query map-range-slider__query--end"
						style={{ left: `${endPct}%` }}>
						{formatShort(value.max)}
					</span>
					<Slider
						range
						allowCross={false}
						draggableTrack
						min={boundMin}
						max={boundMax}
						value={[value.min, value.max]}
						onChange={applyLocal}
						onChangeComplete={commit} />
				</div>
				<span className="map-range-slider__scale map-range-slider__scale--end">
					{formatShort(boundMax)}
				</span>
			</div>
		</div>
	);
};

MapRangeSlider.propTypes = {
	minDate: PropTypes.number,
	maxDate: PropTypes.number
};

export default MapRangeSlider;
